import { ChevronRight, Plug } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { IntegrationConnectorRef } from "@/api";
import { Text } from "@/components/Text";

/**
 * An integration's own connectors that hold tools (its MCP server, e.g. HubSpot's), each linking to the connector's
 * Tools tab where admins turn tools on or off and choose which ask first (MHS-1080).
 */
export function IntegrationConnectorTools({ connectors }: Readonly<{ connectors: IntegrationConnectorRef[] }>) {
  const { t } = useTranslation("adminIntegrations");
  return (
    <ul aria-label={t("detail.connectorTools")} className="flex flex-col gap-2">
      {connectors.map((connector) => (
        <li key={connector.uuid}>
          <Link
            to={`/build/connectors/${connector.uuid}/tools`}
            className="flex items-center gap-3 rounded-lg border border-border-subtle px-4 py-3 hover:bg-surface-secondary btn-focus-ring"
          >
            <Plug size={16} className="shrink-0 text-content-tertiary" aria-hidden />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <Text as="span" variant="body-sm" weight="semibold">
                {t("detail.manageTools", { name: connector.name })}
              </Text>
              <Text as="span" variant="caption" tone="secondary">
                {connector.toolCount
                  ? t("detail.connectorToolCount", {
                      count: connector.toolCount,
                      enabled: connector.enabledToolCount ?? 0,
                    })
                  : t("detail.connectorNoToolsYet")}
              </Text>
              {connector.url && (
                <Text as="span" variant="caption" tone="tertiary" className="truncate">
                  {connector.url}
                </Text>
              )}
            </span>
            <ChevronRight size={16} className="shrink-0 text-content-tertiary" aria-hidden />
          </Link>
        </li>
      ))}
    </ul>
  );
}
